/**
 * F37 (Wave 2D) — admin review + endorsement moderation queue.
 *
 * Route: `/review-moderation`
 * Permission: `reviews:moderate` (the reviews tab) — gates the page render.
 * The endorsements tab additionally needs `endorsements:moderate`; it's hidden
 * for actors who lack it.
 *
 * Two tabs:
 *   - Flagged reviews — TRUE-identity rows (admin always sees real names, D3);
 *     status filter (Flagged / Removed / All); row → /review-moderation/:id.
 *   - Reported endorsements — active/revoked badge rows with grantor + recipient.
 *
 * Cursor-paginated via the venue-claims Load-more pattern. The sidebar
 * pending-flagged badge is polled separately by `NavCountsProvider`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Chip,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type {
  AdminPageMeta,
  PlayerBadge,
  ReviewStatus,
  TrainerReviewAdmin,
} from '@bravemove/types';
import {
  adminRecommendationApi,
  type AdminReviewStatusFilter,
  type ModerateEndorsementInput,
} from '../../api/recommendation';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';
import { AdminPagination } from '../../components/AdminPagination';
import { ModerateEndorsementDialog } from '../../components/recommendation/ModerateEndorsementDialog';
import { InertTick } from '../../components/verification/EntityVerificationCard';
import { useAuth } from '../../auth/useAuth';

const PAGE_LIMIT = 50;

type ActiveTab = 'reviews' | 'endorsements';

const REVIEW_FILTERS: ReadonlyArray<{ value: AdminReviewStatusFilter; defaultLabel: string }> = [
  { value: 'flagged', defaultLabel: 'Flagged' },
  { value: 'removed', defaultLabel: 'Removed' },
  { value: 'published', defaultLabel: 'Published' },
  { value: 'all', defaultLabel: 'All' },
];

const REVIEW_STATUS_CHIP: Record<ReviewStatus, StatusChipStatus> = {
  published: 'approved',
  flagged: 'in_review',
  removed: 'rejected',
};

function ratingStars(rating: number): string {
  return '★'.repeat(Math.max(0, Math.min(5, rating))).padEnd(5, '☆');
}

export function ReviewModerationQueuePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const canModerateEndorsements =
    auth.adminUser?.permissions.includes('endorsements:moderate') ?? false;

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as ActiveTab | null) ?? 'reviews';
  const reviewFilter = (searchParams.get('status') as AdminReviewStatusFilter | null) ?? 'flagged';
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [reviews, setReviews] = useState<readonly TrainerReviewAdmin[]>([]);
  const [endorsements, setEndorsements] = useState<readonly PlayerBadge[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** The badge being moderated via the reason dialog (TCR-006) — null = closed. */
  const [endorsementTarget, setEndorsementTarget] = useState<PlayerBadge | null>(null);
  const [endorsementError, setEndorsementError] = useState<string | null>(null);

  const fetchReviews = useCallback(
    async (page: number) => {
      setLoading(true);
      setReviews([]);
      setError(null);
      try {
        const data = await adminRecommendationApi.listReviews({
          ...(reviewFilter !== 'all' ? { status: reviewFilter } : {}),
          page,
          perPage: PAGE_LIMIT,
        });
        setReviews(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [reviewFilter, t],
  );

  const fetchEndorsements = useCallback(
    async (page: number) => {
      setLoading(true);
      setEndorsements([]);
      setError(null);
      try {
        const data = await adminRecommendationApi.listEndorsements({
          page,
          perPage: PAGE_LIMIT,
        });
        setEndorsements(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (tab === 'reviews') void fetchReviews(pageParam);
    else void fetchEndorsements(pageParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, reviewFilter, pageParam]);

  const setTab = (next: ActiveTab) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('tab', next);
      sp.delete('status');
      sp.delete('page');
      return sp;
    });
  };

  const setReviewFilter = (value: AdminReviewStatusFilter) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('tab', 'reviews');
      if (value !== 'flagged') sp.set('status', value);
      else sp.delete('status');
      sp.delete('page');
      return sp;
    });
  };

  const goToPage = (page: number) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (page <= 1) sp.delete('page');
      else sp.set('page', String(page));
      return sp;
    });
  };

  /**
   * Remove/Restore go through the shared `ModerateEndorsementDialog` (TCR-006)
   * — the admin supplies a real audit-logged reason + gets a confirmation
   * step, matching every other destructive moderation action in this group.
   * The previous implementation fired immediately with a canned hardcoded
   * reason and no confirmation.
   */
  const handleEndorsementModerate = async (input: ModerateEndorsementInput) => {
    if (!endorsementTarget) return;
    setEndorsementError(null);
    try {
      await adminRecommendationApi.moderateEndorsement(endorsementTarget.id, input);
      toast.push({
        tone: 'success',
        title: t('admin:recommendation.moderation.endorsement_done', { defaultValue: 'Updated' }),
      });
      setEndorsementTarget(null);
      await fetchEndorsements(pageParam);
    } catch (err) {
      // Keep the dialog open so the admin can correct + retry.
      setEndorsementError(translateApiError(err, t));
    }
  };

  const reviewColumns = useMemo<readonly TableColumn<TrainerReviewAdmin>[]>(
    () => [
      {
        key: 'parties',
        header: t('admin:recommendation.moderation.column_parties', { defaultValue: 'Review' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="inline-flex flex-wrap items-center gap-1 text-[12.5px] text-ink">
              {row.playerDisplayName}
              {/* F09 v2 (§13 row 21) — the TRUE author's player tick, inert.
                  Admins see real identity, so it shows even when anonymous. */}
              <InertTick
                verified={row.authorVerified}
                entityType="player"
                testId={`review-author-tick-${row.id}`}
              />
              {row.anonymous ? (
                <Badge tone="neutral" size="sm" className="ms-1.5">
                  {t('admin:recommendation.moderation.anonymous', { defaultValue: 'Anonymous' })}
                </Badge>
              ) : null}
            </span>
            <span className="inline-flex flex-wrap items-center gap-1 text-[11px] text-ink-3">
              {t('admin:recommendation.moderation.about', { defaultValue: 'about' })}{' '}
              {row.trainerDisplayName}
              {/* F09 v2 (§13 row 21) — the reviewed trainer's tick, inert. */}
              <InertTick
                verified={row.trainerVerified}
                entityType="trainer"
                testId={`review-trainer-tick-${row.id}`}
              />{' '}
              • <span className="capitalize">{row.sportName}</span>
            </span>
          </div>
        ),
      },
      {
        key: 'rating',
        header: t('admin:recommendation.moderation.column_rating', { defaultValue: 'Rating' }),
        cell: (row) => (
          <span
            className="font-mono text-[12px] text-warning tabular-nums"
            aria-label={`${row.rating}/5`}
          >
            {ratingStars(row.rating)}
          </span>
        ),
      },
      {
        key: 'flag',
        header: t('admin:recommendation.moderation.column_flag', { defaultValue: 'Flag reason' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2 line-clamp-2 max-w-[36ch]">
            {row.flagReason ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('recommendation:moderation.status_label', { defaultValue: 'Status' }),
        cell: (row) => (
          <div className="flex items-center gap-1.5">
            <StatusChip
              size="sm"
              status={REVIEW_STATUS_CHIP[row.status] ?? 'pending'}
              pulse={row.status === 'flagged'}
              label={t(`recommendation:moderation.status.${row.status}`, {
                defaultValue: row.status,
              })}
            />
            {row.adminLocked ? (
              <Badge tone="warning" size="sm">
                {t('admin:recommendation.moderation.locked', { defaultValue: 'Locked' })}
              </Badge>
            ) : null}
          </div>
        ),
      },
    ],
    [t],
  );

  const endorsementColumns = useMemo<readonly TableColumn<PlayerBadge>[]>(
    () => [
      {
        key: 'badge',
        header: t('admin:recommendation.moderation.column_badge', { defaultValue: 'Badge' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[12.5px] text-ink">{row.badgeName}</span>
            <span className="font-mono text-[11px] text-ink-3">{row.badgeCode}</span>
          </div>
        ),
      },
      {
        key: 'parties',
        header: t('admin:recommendation.moderation.column_grant', { defaultValue: 'Grant' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[12px] text-ink">
              {row.attribution.trainerDisplayName ??
                t('admin:recommendation.moderation.unknown_trainer', {
                  defaultValue: 'Unknown',
                })}{' '}
              → <span className="font-mono text-[11px] text-ink-3">{row.userId}</span>
            </span>
            {row.sportName ? (
              <span className="text-[11px] text-ink-3 capitalize">{row.sportName}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'status',
        header: t('recommendation:moderation.status_label', { defaultValue: 'Status' }),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={row.revoked ? 'rejected' : 'approved'}
            label={
              row.revoked
                ? t('admin:recommendation.moderation.endorsement_revoked', {
                    defaultValue: 'Revoked',
                  })
                : t('admin:recommendation.moderation.endorsement_active', {
                    defaultValue: 'Active',
                  })
            }
          />
        ),
      },
      ...(canModerateEndorsements
        ? [
            {
              key: 'actions',
              header: t('admin:recommendation.catalog.actions', { defaultValue: 'Actions' }),
              align: 'end' as const,
              cell: (row: PlayerBadge) => (
                <div className="flex justify-end gap-2">
                  {row.revoked ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEndorsementError(null);
                        setEndorsementTarget(row);
                      }}
                      data-testid={`endorsement-restore-${row.id}`}
                    >
                      {t('recommendation:moderation.action.restore', { defaultValue: 'Restore' })}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEndorsementError(null);
                        setEndorsementTarget(row);
                      }}
                      data-testid={`endorsement-remove-${row.id}`}
                    >
                      {t('recommendation:moderation.action.remove', { defaultValue: 'Remove' })}
                    </Button>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [t, canModerateEndorsements],
  );

  return (
    <div className="space-y-6" data-testid="review-moderation-page">
      <PageHeader
        eyebrow={t('admin:recommendation.moderation.eyebrow', { defaultValue: 'Moderation' })}
        title={t('admin:recommendation.moderation.title', {
          defaultValue: 'Reviews & endorsements',
        })}
        subtitle={t('admin:recommendation.moderation.subtitle', {
          defaultValue: 'Flagged reviews and reported endorsements — you always see true identity.',
        })}
      />

      <div
        role="tablist"
        aria-label={t('admin:recommendation.moderation.tabs_label', { defaultValue: 'Surface' })}
        className="flex flex-wrap items-center gap-2"
      >
        <Chip selected={tab === 'reviews'} onClick={() => setTab('reviews')}>
          {t('recommendation:moderation.queue_title', { defaultValue: 'Flagged reviews' })}
        </Chip>
        {canModerateEndorsements ? (
          <Chip selected={tab === 'endorsements'} onClick={() => setTab('endorsements')}>
            {t('recommendation:moderation.endorsement_queue_title', {
              defaultValue: 'Reported endorsements',
            })}
          </Chip>
        ) : null}
      </div>

      {tab === 'reviews' ? (
        <div
          role="tablist"
          aria-label={t('admin:recommendation.moderation.filter_label', {
            defaultValue: 'Filter by status',
          })}
          className="flex flex-wrap items-center gap-2"
        >
          {REVIEW_FILTERS.map((f) => (
            <Chip
              key={f.value}
              selected={reviewFilter === f.value}
              onClick={() => setReviewFilter(f.value)}
            >
              {/* The All chip gets its own key (TCR-007) — mapping 'all' →
                  the 'published' status key rendered two chips both labelled
                  "Published". */}
              {t(
                f.value === 'all'
                  ? 'recommendation:moderation.status_filter_all'
                  : `recommendation:moderation.status.${f.value}`,
                { defaultValue: f.defaultLabel },
              )}
            </Chip>
          ))}
        </div>
      ) : null}

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="review-moderation-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : tab === 'reviews' ? (
        reviews.length === 0 ? (
          <EmptyState
            icon={<UIIcon name="shield" size={36} color="currentColor" />}
            title={t('admin:recommendation.moderation.reviews_empty', {
              defaultValue: 'No reviews in this view',
            })}
          />
        ) : (
          <Table
            data={reviews as TrainerReviewAdmin[]}
            columns={reviewColumns as TableColumn<TrainerReviewAdmin>[]}
            rowKey={(row) => row.id}
            onRowPress={(row) => navigate(`/review-moderation/${row.id}`)}
            empty={t('admin:recommendation.moderation.reviews_empty', {
              defaultValue: 'No reviews',
            })}
          />
        )
      ) : endorsements.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="star" size={36} color="currentColor" />}
          title={t('admin:recommendation.moderation.endorsements_empty', {
            defaultValue: 'No endorsements in this view',
          })}
        />
      ) : (
        <Table
          data={endorsements as PlayerBadge[]}
          columns={endorsementColumns as TableColumn<PlayerBadge>[]}
          rowKey={(row) => row.id}
          empty={t('admin:recommendation.moderation.endorsements_empty', {
            defaultValue: 'No endorsements',
          })}
        />
      )}

      {pagination && !loading && (tab === 'reviews' ? reviews.length : endorsements.length) > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={tab === 'reviews' ? reviews.length : endorsements.length}
          onChange={goToPage}
          testId="review-moderation-pagination"
        />
      ) : null}

      <ModerateEndorsementDialog
        open={endorsementTarget !== null}
        badge={
          endorsementTarget
            ? {
                id: endorsementTarget.id,
                label: `${endorsementTarget.badgeName} — ${
                  endorsementTarget.attribution.trainerDisplayName ??
                  t('admin:recommendation.moderation.unknown_trainer', { defaultValue: 'Unknown' })
                } → ${endorsementTarget.userId}`,
                revoked: endorsementTarget.revoked,
              }
            : null
        }
        onConfirm={handleEndorsementModerate}
        onClose={() => {
          setEndorsementTarget(null);
          setEndorsementError(null);
        }}
        errorMessage={endorsementError}
      />
    </div>
  );
}

export default ReviewModerationQueuePage;
